// API Configuration
// REACT_APP_API_URL should be the base URL (e.g., http://localhost:3005)
// We normalize it to remove any trailing /api, then append /api for API endpoints
const normalizeBaseUrl = (url) => {
  // If we are in the browser and not on localhost, use current origin
  if (typeof window !== 'undefined' && 
      window.location.hostname !== 'localhost' && 
      window.location.hostname !== '127.0.0.1') {
    return window.location.origin;
  }
  
  // Default to local backend when env var is missing or we are on localhost
  if (!url) return 'http://localhost:3005';
  
  // Remove trailing /api if present and trailing slashes
  return url.replace(/\/api\/?$/, '').replace(/\/+$/, '');
};
export const BASE_URL = normalizeBaseUrl(process.env.REACT_APP_API_URL);
export const API_BASE_URL = `${BASE_URL}/api`.replace(/([^:]\/)\/+/g, '$1');

/** Build full URL for an uploads image path. Avoids duplicate /uploads/uploads/. */
export const getUploadsImageUrl = (image) => {
  if (!image || typeof image !== 'string') return null;
  const v = image.trim();
  if (!v) return null;
  if (v.startsWith('http')) return v;
  if (v.startsWith('/uploads/')) return BASE_URL + v;
  if (v.startsWith('uploads/')) return BASE_URL + '/' + v;
  return BASE_URL + '/uploads/blog/' + v;
};

/** Placeholder image when upload fails or is missing. */
export const UPLOADS_FALLBACK_IMAGE = `${process.env.PUBLIC_URL || ''}/images/no-image.png`;

// Helper function to normalize endpoint (remove leading slashes to prevent double slashes)
const normalizeEndpoint = (endpoint) => {
  if (!endpoint) return '';
  // Remove leading slashes
  return endpoint.replace(/^\/+/, '');
};

// Helper function to make API requests
export const apiRequest = async (endpoint, options = {}) => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  // Ensure no double slashes in URL
  const url = `${API_BASE_URL.replace(/\/+$/, '')}/${normalizedEndpoint}`.replace(/([^:]\/)\/+/g, '$1');
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const isDev = process.env.NODE_ENV === 'development';
  try {
    if (isDev) console.log(`[API] Making request to: ${url}`);
    const response = await fetch(url, config);

    if (!response) {
      throw new Error('Network error: No response from server. Please ensure the backend server is running on http://localhost:3005');
    }

    if (isDev) console.log(`[API] Response status: ${response.status}`);

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    } catch (parseError) {
      if (isDev) console.error('[API] Failed to parse response as JSON:', parseError);
      throw new Error(`Invalid response from server: ${response.status} ${response.statusText}`);
    }

    if (Array.isArray(data) && isDev) console.log(`[API] Response array length: ${data.length}`);

    if (!response.ok) {
      if (isDev) console.error('[API] Request failed:', data);
      throw new Error(data?.error || `API request failed: ${response.status} ${response.statusText}`);
    }

    return data;
  } catch (error) {
    if (isDev) {
      console.error('[API] Request error:', error);
      console.error('[API] Error URL:', url);
    }
    
    // Provide more helpful error messages
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Failed to fetch: Please ensure the backend server is running on http://localhost:3005');
    }
    
    throw error;
  }
};

// In-memory cache for products to prevent duplicate API calls (TTL 60s)
let _productsCache = null;
let _productsCacheTime = 0;
const PRODUCTS_CACHE_TTL_MS = 60000;

// Products API - Real API implementation
export const productsAPI = {
  getAll: async () => {
    const now = Date.now();
    if (_productsCache && now - _productsCacheTime < PRODUCTS_CACHE_TTL_MS) {
      return _productsCache;
    }
    const data = await apiRequest('/products');
    _productsCache = data;
    _productsCacheTime = now;
    return data;
  },
  clearCache: () => {
    _productsCache = null;
    _productsCacheTime = 0;
  },
  getById: (id) => apiRequest(`/products/${id}`),
  create: (data, token) => apiRequest('/products', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  }),
  update: (id, data, token) => {
    const options = {
      method: 'PUT',
      body: JSON.stringify(data),
    };
    if (token) {
      options.headers = {
        'Authorization': `Bearer ${token}`,
      };
    }
    return apiRequest(`/products/${id}`, options);
  },
  delete: (id, token) => apiRequest(`/products/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  }),
  // Add child under parentId anywhere in tree (returns created child with id)
  addChild: (parentProductId, parentNodeId, childPayload) => apiRequest(`/products/${parentProductId}/children`, {
    method: 'POST',
    body: JSON.stringify({
      parentNodeId,
      child: childPayload
    }),
  }),
};

// Nodes API - For hierarchical navigation
export const nodesAPI = {
  // Get all nodes for a product (depth=1 for root children only)
  getByProduct: (productId, depth) => {
    const query = depth ? `?depth=${depth}` : '';
    return apiRequest(`/products/${productId}/nodes${query}`);
  },
  // Get a node by slug within a product
  getBySlug: (productId, slug) => 
    apiRequest(`/products/${productId}/nodes/by-slug?slug=${encodeURIComponent(slug)}`),
  // Get children of a specific node
  getChildren: (productId, nodeId) => 
    apiRequest(`/nodes/${nodeId}/children?productId=${productId}`),
  // Create root child
  createRootChild: (productId, nodeData, token) => 
    apiRequest(`/products/${productId}/nodes`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: JSON.stringify(nodeData),
    }),
  // Create child under a node (uses simpler /api/nodes/[nodeId]/children endpoint)
  createChild: (productId, parentId, nodeData, token) => 
    apiRequest(`/nodes/${parentId}/children`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: JSON.stringify({ ...nodeData, productId }),
    }),
  // Update node
  update: (nodeId, updates, token) => 
    apiRequest(`/nodes/${nodeId}`, {
      method: 'PATCH',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: JSON.stringify(updates),
    }),
  // Delete node
  delete: (nodeId, token) => 
    apiRequest(`/nodes/${nodeId}`, {
      method: 'DELETE',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    }),
  // Move node
  move: (nodeId, newParentId, token) => 
    apiRequest('/nodes/move', {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: JSON.stringify({ nodeId, newParentId }),
    }),
};


